import '@/modules/real-time/scss/realtime-dashboard.scss';
import { useRealtimeDashboardViewModel } from '@/modules/real-time/viewmodels/realtime-dashboard.view-model';
import { Bar, Column } from '@ant-design/charts';
import { useTrackReportViewed } from '@/core/analytics';
import { Overall } from '../components/Overall';
import { formatNumber, formatCurrency, formatNumberWithCommas } from '../helpers/format.helper';

const REALTIME_REPORT = {
  id: 'real-time-call-metrics',
  name: 'Real-time call metrics',
  url: '/real-time/call-metrics',
  type: 'realtime',
} as const;

export const RealtimeDashboard = () => {
  const { filter, setFilter, realtimeMetric } = useRealtimeDashboardViewModel(10000);
  useTrackReportViewed(REALTIME_REPORT);

  return (
    <div className='p-4 bg-gray-50 min-h-screen'>
      <Overall
        call={realtimeMetric.saleMetric.callMetric.overall}
        performance={realtimeMetric.saleMetric.performanceMetric.overall}
        filter={filter}
        setFilter={setFilter}
      />

      <div className='grid grid-cols-10 grid-rows-7 gap-3 mt-4'>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-1 col-end-3 row-start-1 row-end-5'>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top đầu và cuối BAs theo số cuộc gọi nhấc máy
          </h4>
          <Bar
            height={400}
            theme='academy'
            xField='businessAreaName'
            yField='totalPickUpCall'
            data={realtimeMetric.saleMetric.callMetric.topBusinessAreas.map((item) => ({
              ...item,
              totalPickUpCall: Number(item.totalPickUpCall),
            }))}
            colorField='color'
            legend={false}
            style={{
              fill: ({ rank }: any) => {
                if (rank <= 5) {
                  return '#e31f26';
                }
                return '#000000';
              },
            }}
            axis={{
              y: {
                labelFormatter: (v: any) => formatNumberWithCommas(v),
              },
            }}
            tooltip={(d) => ({
              name: 'Số cuộc gọi nhấc máy',
              value: formatNumberWithCommas(d.totalPickUpCall),
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-3 col-end-5 row-start-1 row-end-5'>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top đầu và cuối BUs theo số cuộc gọi nhấc máy
          </h4>
          <Bar
            height={400}
            theme='academy'
            xField='businessUnitName'
            yField='totalPickUpCall'
            data={realtimeMetric.saleMetric.callMetric.topBusinessUnits.map((item) => ({
              ...item,
              totalPickUpCall: Number(item.totalPickUpCall),
            }))}
            colorField='color'
            legend={false}
            style={{
              fill: ({ rank }: any) => {
                if (rank <= 5) {
                  return '#e31f26';
                }
                return '#000000';
              },
            }}
            axis={{
              y: {
                labelFormatter: (v: any) => formatNumberWithCommas(v),
              },
            }}
            tooltip={(d) => ({
              name: 'Số cuộc gọi nhấc máy',
              value: formatNumberWithCommas(d.totalPickUpCall),
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-1 col-end-5 row-start-5 row-end-8'>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 10 Saleman theo số cuộc gọi nhấc máy
          </h4>
          <Column
            className='h-full'
            colorField={'#e31f26'}
            data={realtimeMetric.saleMetric.callMetric.topSaleMans}
            xField='userName'
            yField='totalCall'
            theme='academy'
            axis={{
              x: {
                style: {
                  labelTransform: 'rotate(35)',
                },
              },
            }}
            tooltip={(d) => ({
              name: 'Số cuộc gọi nhấc máy',
              value: formatNumberWithCommas(d.totalCall),
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-5 col-end-7 row-start-1 row-end-3 '>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 3 BAs theo doanh thu
          </h4>
          <Bar
            className='h-full'
            colorField={'#e31f26'}
            theme='academy'
            xField='businessAreaName'
            yField='revenueAchieved'
            axis={{
              y: {
                labelFormatter: (v: any) => formatNumber(v),
                style: {
                  labelTransform: 'rotate(45)',
                },
              },
            }}
            tooltip={(d) => ({
              name: 'Doanh thu',
              value: formatCurrency(d.revenueAchieved),
            })}
            data={realtimeMetric.saleMetric.performanceMetric.revenue.top3BusinessUnits}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-7 col-end-9 row-start-1 row-end-3 '>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 3 BAs theo tiến độ KPI doanh thu
          </h4>
          <Bar
            className='h-full'
            colorField={'#e31f26'}
            theme='academy'
            xField='businessAreaName'
            yField='kpiProgress'
            data={realtimeMetric.saleMetric.performanceMetric.kpiRevenue.top3BusinessUnits}
            axis={{
              y: {
                labelFormatter: (v: any) => `${(v * 100).toFixed(1)}%`,
                style: {
                  labelTransform: 'rotate(45)',
                },
              },
            }}
            tooltip={(d) => ({
              name: 'Tiến độ KPI',
              value: `${(d.kpiProgress * 100).toFixed(1)}%`,
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-9 col-end-11 row-start-1 row-end-3 '>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 3 BAs theo CR45
          </h4>
          <Bar
            className='h-full'
            colorField={'#e31f26'}
            theme='academy'
            xField='businessAreaName'
            yField='cr45'
            data={realtimeMetric.saleMetric.performanceMetric.cr45.top3BusinessUnits}
            axis={{
              y: {
                labelFormatter: (v: any) => `${(v * 100).toFixed(1)}%`,
                style: { labelTransform: 'rotate(45)' },
              },
            }}
            tooltip={(d) => ({
              name: 'CR45',
              value: `${(d.cr45 * 100).toFixed(1)}%`,
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-5 col-end-7 row-start-3 row-end-5 '>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 5 BUs theo tiến độ doanh thu
          </h4>
          <Bar
            className='h-full'
            colorField={'#e31f26'}
            theme='academy'
            xField='businessUnitName'
            yField='revenueAchieved'
            data={realtimeMetric.saleMetric.performanceMetric.revenue.top5BusinessUnits}
            axis={{
              y: {
                labelFormatter: (v: any) => formatNumber(v),
                style: {
                  labelTransform: 'rotate(45)',
                },
              },
            }}
            tooltip={(d) => ({
              name: 'Doanh thu',
              value: formatCurrency(d.revenueAchieved),
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-7 col-end-9 row-start-3 row-end-5 '>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 5 BUs theo tiến độ KPI doanh thu
          </h4>
          <Bar
            className='h-full'
            colorField={'#e31f26'}
            theme='academy'
            xField='businessUnitName'
            yField='kpiProgress'
            data={realtimeMetric.saleMetric.performanceMetric.kpiRevenue.top5BusinessUnits}
            axis={{
              y: {
                labelFormatter: (v: any) => `${(v * 100).toFixed(1)}%`,
                style: { labelTransform: 'rotate(45)' },
              },
            }}
            tooltip={(d) => ({
              name: 'Tiến độ KPI',
              value: `${(d.kpiProgress * 100).toFixed(1)}%`,
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-9 col-end-11 row-start-3 row-end-5 '>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 5 BUs theo CR45
          </h4>
          <Bar
            className='h-full'
            colorField={'#e31f26'}
            theme='academy'
            xField='businessUnitName'
            yField='cr45'
            data={realtimeMetric.saleMetric.performanceMetric.cr45.top5BusinessUnits}
            axis={{
              y: {
                labelFormatter: (v: any) => `${(v * 100).toFixed(1)}%`,
                style: { labelTransform: 'rotate(45)' },
              },
            }}
            tooltip={(d) => ({
              name: 'CR45',
              value: `${(d.cr45 * 100).toFixed(1)}%`,
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-5 col-end-8 row-start-5 row-end-8'>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 10 Saleman theo doanh thu
          </h4>
          <Column
            className='h-full'
            colorField={'#e31f26'}
            data={realtimeMetric.saleMetric.performanceMetric.revenue.top10Salemans}
            xField='userName'
            yField='revenueAchieved'
            theme='academy'
            axis={{
              y: {
                labelFormatter: (v: any) => formatNumber(v),
              },
              x: {
                labelTransform: 'rotate(45)',
              },
            }}
            tooltip={(d) => ({
              name: 'Doanh thu',
              value: formatCurrency(d.revenueAchieved),
            })}
          />
        </div>
        <div className='bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 col-start-8 col-end-11 row-start-5 row-end-8'>
          <h4 className='text-gray-800 font-semibold mb-2 text-sm border-b pb-2'>
            Top 10 Saleman theo KPI doanh thu
          </h4>
          <Column
            className='h-full'
            colorField={'#e31f26'}
            data={realtimeMetric.saleMetric.performanceMetric.kpiRevenue.top10Salemans}
            xField='userName'
            yField='kpiProgress'
            theme='academy'
            axis={{
              y: {
                labelFormatter: (v: any) => `${(v * 100).toFixed(1)}%`,
              },
              x: {
                labelTransform: 'rotate(45)',
              },
            }}
            tooltip={(d) => ({
              name: 'Tiến độ KPI',
              value: `${(d.kpiProgress * 100).toFixed(1)}%`,
            })}
          />
        </div>
      </div>
    </div>
  );
};
